import { useEffect, useRef, useState } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-32 bg-white"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <span className="inline-block text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">About Me</span>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Let Me Introduce Myself
          </h2>
        </div>

        {/* Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Large Card - Top Left */}
          <div
            className={`lg:col-span-2 bg-slate-50 border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <p className="text-lg text-slate-900 leading-relaxed mb-6">
              <span className="text-2xl font-bold text-slate-900">
                Hi, I'm Charit Patel,
              </span>
              <br />
              <span className="text-xl">
                a UI/UX designer who enjoys turning complex ideas into simple, intuitive digital experiences.
              </span>
            </p>
            <p className="text-lg text-slate-700 leading-relaxed">
              With a background in Computer Science Engineering and UX Design training from Humber Polytechnic, I combine technical understanding with a deep focus on human centered design.
            </p>
          </div>

          {/* Small Card - Top Right */}
          <div
            className={`bg-slate-50 border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 flex flex-col justify-center ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-[#6b3d52] mb-4 mx-auto">
              <i className="ri-heart-pulse-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3 text-center">What Drives Me</h3>
          </div>

          {/* Medium Card - Bottom Left */}
          <div
            className={`bg-slate-50 border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#6b3d52] flex-shrink-0">
                <i className="ri-compass-3-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Beyond Design</h3>
                <p className="text-base text-slate-700 leading-relaxed">
                  When I'm not designing, I'm exploring new tools, studying product patterns, or discovering creative inspiration around Toronto.
                </p>
              </div>
            </div>
          </div>

          {/* Large Card - Bottom Right */}
          <div
            className={`lg:col-span-2 bg-slate-50 border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#6b3d52] flex-shrink-0">
                <i className="ri-lightbulb-flash-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">My Approach</h3>
                <p className="text-lg text-slate-700 leading-relaxed">
                  I'm driven by curiosity, empathy, and the challenge of making digital products feel effortless. I love simplifying workflows, refining interactions, and creating interfaces that solve real problems while feeling thoughtful and human. I'm always learning and always excited for the next design challenge.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
